let AWS = require('aws-sdk');
let KVSWebRtc = require('amazon-kinesis-video-streams-webrtc');

import { setInterval, clearInterval, setTimeout } from 'worker-timers';

let log = {
    log: (message) => console.log(`🗨️ %c${message}`, 'color: blue'),
    error: (message) => console.error(`🪳 %c${message}`, 'color: red; font-weight:bold'),
    success: (message) => console.log(`✅ %c${message}`, 'color: green'),
    warning: (message) => console.warn(`🟠 %c${message}`, 'color: orange'),
};

class KinesisProducerException extends Error {
    constructor(message) {
        super(message);
        this.name = "KinesisProducerException";
    }
}

class Kinesis {
    /**
     * @param {object} options
     * @param {string} options.region
     * @param {string} options.accessKeyId
     * @param {string} options.secretAccessKey
     * @param {string} options.sessionToken
     * @param {string} options.channelName
     * @param {string} options.streamName
     * @param {boolean} options.forceTURN
     * @param {boolean} options.useTrickleICE
     * @param {boolean} options.natTransversalDisabled
     */
    constructor(options = {}) {
        this.options = {
            region: process.env.VUE_APP_AWS_REGION,
            accessKeyId: null,
            secretAccessKey: null,
            sessionToken: null,
            channelName: null,
            streamName: null,
            forceTURN: false,
            useTrickleICE: true,
            natTransversalDisabled: false,
            ...options,
        };
    }

    async createChannel() {
        return await retryAsync(() => this._createOrGetChannelARN(), this.options.testFinished, 2000);
    }

    _createKinesisVideoClient(endpoint = null, useCorrectClockSkew = false) {
        const { region, accessKeyId, secretAccessKey, sessionToken } = this.options;
        return new AWS.KinesisVideo({
            region: region,
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
            sessionToken: sessionToken,
            ...(endpoint && { endpoint: endpoint }),
            ...(useCorrectClockSkew && { correctClockSkew: true }),
        });
    }

    async _createOrGetChannelARN() {
        const stream = await this._createOrGetStream();
        const signalingClient = this._createKinesisVideoClient(stream.DataEndpoint);
        const channel = await this._createOrGetSignalingChannel(signalingClient);
        return channel.ChannelARN;
    }

    async _createOrGetStream() {
        const kinesisVideo = this._createKinesisVideoClient();

        const { streamName } = this.options;
        try {
            const stream = await kinesisVideo.createStream({ StreamName: streamName, DataRetentionInHours: 24 }).promise();
            log.success(`Stream created: ${streamName}`);
            return stream;
        } catch (error) {
            if (error.code === 'ResourceInUseException') {
                log.warning(`Stream ${streamName} already exists. Retrieving existing stream.`);
                return await this._describeStream(kinesisVideo, streamName);
            } else {
                throw new KinesisProducerException(`Error creating stream ${streamName}: ${error.message}`);
            }
        }
    }

    async _describeStream(kinesisVideo, streamName) {
        try {
            const result = await kinesisVideo.describeStream({ StreamName: streamName }).promise();
            return result.StreamInfo;
        } catch (error) {
            throw new KinesisProducerException(`Error describing stream ${streamName}: ${error.message}`);
        }
    }

    async _createOrGetSignalingChannel(kinesisVideo) {
        const { channelName } = this.options;
        try {
            const channel = await kinesisVideo.createSignalingChannel({ ChannelName: channelName }).promise();
            log.success(`Signaling channel created: ${channelName}`);
            return channel;
        } catch (error) {
            if (error.code === 'ResourceInUseException') {
                log.warning(`Signaling channel ${channelName} already exists. Retrieving existing channel.`);
                return await this._describeSignalingChannel(kinesisVideo, channelName);
            } else {
                throw new KinesisProducerException(`Error creating signaling channel ${channelName}: ${error.message}`);
            }
        }
    }

    async _describeSignalingChannel(kinesisVideo, channelName) {
        try {
            const result = await kinesisVideo.describeSignalingChannel({ ChannelName: channelName }).promise();
            return result.ChannelInfo;
        } catch (error) {
            throw new KinesisProducerException(`Error describing signaling channel ${channelName}: ${error.message}`);
        }
    }

    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

class StreamProducer {
    /**
     * @param {string} channelARN
     * @param {Kinesis} kinesisConfigInstance
     * @param {MediaStream} localStream
     * @param {object} options
     * @param {function} options.onError
     * @param {number} options.maxRetries
     */
    constructor(channelARN, kinesisConfigInstance, localStream, options = {}) {
        this.signalingClient = null;
        this.channelARN = channelARN;
        this.peerConnectionByClientId = {};
        this.dataChannelByClientId = {};
        this.localStream = localStream;
        this.peerConnectionStatsInterval = null;
        this.kinesis = kinesisConfigInstance;
        this._closingManually = false;
        this._wsPingInterval = null;
        this.options = {
            onError: null,
            ...options,
        };
        this._onStatsReport  = null;
        this._openDataChannel = null;
    }

    async start(onStatsReport, openDataChannel) {
        try {
            this._onStatsReport  = onStatsReport;
            this._openDataChannel = openDataChannel;
            this.stop();
            await retryAsync(() => this._initializeSignalingClient(), this.options.testFinished);
            this._setupEventHandlers(onStatsReport, openDataChannel);
            this.signalingClient.open();
        } catch (error) {
            log.error(`Failed to start StreamProducer: ${error.message}`);
            if (this.options.onError) {
                this.options.onError(error);
            }
        }
        window.addEventListener('beforeunload', () => this.stop());
    }
    async refreshCredentials(newKinesis) {
        try{
            this._closingManually = true;
            this._closeSignalingClient();
        } catch (err) {
            return;
        } finally {
            this._closingManually = false;
        }

        this.kinesis = newKinesis;
        await this._initializeSignalingClient();
        this._setupEventHandlers(this._onStatsReport, this._openDataChannel);
        this.signalingClient.open();
    }

    stop() {
        this._closingManually = true;
        this._closeSignalingClient();
        this._clearPeerConnections();
        this._clearStatsInterval();
        this._clearPingInterval();
        this._closingManually = false;
    }

    async _initializeSignalingClient() {
        const { region, accessKeyId, secretAccessKey, sessionToken } = this.kinesis.options;
        const videoClient = new AWS.KinesisVideo({
            region: region,
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
            sessionToken: sessionToken,
            correctClockSkew: true,
        });

        const endpointsByProtocol = await this._getSignalingEndpoints(videoClient);
        this.signalingClient = new KVSWebRtc.SignalingClient({
            channelARN: this.channelARN,
            channelEndpoint: endpointsByProtocol.WSS,
            role: KVSWebRtc.Role.MASTER,
            region: region,
            credentials: { accessKeyId: accessKeyId, secretAccessKey: secretAccessKey, sessionToken: sessionToken },
            systemClockOffset: videoClient.config.systemClockOffset,
        });
        this.configuration = await this._getPeerConnectionConfig(endpointsByProtocol.HTTPS);
    }

    async _getSignalingEndpoints(videoClient) {
        const { channelARN } = this;
        const response = await videoClient.getSignalingChannelEndpoint({
            ChannelARN: channelARN,
            SingleMasterChannelEndpointConfiguration: {
                Protocols: ['WSS', 'HTTPS'],
                Role: KVSWebRtc.Role.MASTER,
            },
        }).promise();

        return response.ResourceEndpointList.reduce((endpoints, endpoint) => {
            endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
            return endpoints;
        }, {});
    }

    async _getPeerConnectionConfig(endpoint) {
        const { region, accessKeyId, secretAccessKey, sessionToken, natTransversalDisabled, forceTURN } = this.kinesis.options;
        const signalingChannelsClient = new AWS.KinesisVideoSignalingChannels({
            region: region,
            accessKeyId: accessKeyId,
            secretAccessKey: secretAccessKey,
            sessionToken: sessionToken,
            endpoint: endpoint,
            correctClockSkew: true,
        });

        const response = await signalingChannelsClient.getIceServerConfig({
            ChannelARN: this.channelARN,
        }).promise();

        const iceServers = [];
        if (!natTransversalDisabled && !forceTURN) {
            iceServers.push({ urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` });
        }
        if (!natTransversalDisabled) {
            response.IceServerList.forEach(iceServer => {
                iceServers.push({
                    urls: iceServer.Uris,
                    username: iceServer.Username,
                    credential: iceServer.Password,
                });
            });
        }

        return {
            iceServers,
            iceTransportPolicy: forceTURN ? 'relay' : 'all',
        };
    }

    _setupEventHandlers(onStatsReport, openDataChannel) {
        this.signalingClient.on('open', () => {
        });

        this.signalingClient.on('sdpOffer', async (offer, remoteClientId) => {
            await this._handleSdpOffer(offer, remoteClientId, onStatsReport, openDataChannel);
        });

        this.signalingClient.on('iceCandidate', async (candidate, remoteClientId) => {
            await this._handleIceCandidate(candidate, remoteClientId);
        });

        this.signalingClient.on('close', () => {
            log.log('Signaling client closed');
            if (!this._closingManually && !this.options.testFinished()){
                try {
                    this.signalingClient.open();
                    log.success('Signaling client reopened')
                } catch (err) {
                    log.error('Falha ao reabrir WS;');
                }
            }
        });

        this.signalingClient.on('error', (error) => {
            log.error(`Signaling client error: ${error.message}`);
            this.stop();
            if (this.options.onError) {
                this.options.onError(error);
            }
        });
    }

    async _handleSdpOffer(offer, remoteClientId, onStatsReport, openDataChannel) {
        try {
            const alreadyExists = !!this.peerConnectionByClientId[remoteClientId];
            const peerConnection = alreadyExists ? this.peerConnectionByClientId[remoteClientId] : this._createPeerConnection(remoteClientId, onStatsReport);

            this._syncLocalTracks(peerConnection);

            if (!alreadyExists && openDataChannel) this._setupDataChannel(peerConnection, remoteClientId);

            await peerConnection.setRemoteDescription(offer);
            await this._createAndSendAnswer(peerConnection, remoteClientId);
            peerConnection.addEventListener('iceconnectionstatechange', () => {
                const connectionState = peerConnection.iceConnectionState;
                log.log(`ICE connection state changed to ${connectionState} for client ${remoteClientId}`);
                if (connectionState === 'failed' || connectionState === 'closed') {
                    log.log(`Cleaning up peer connection for client ${remoteClientId}`);
                    this._cleanupPeerConnection(remoteClientId);
                }
            });

            peerConnection.addEventListener("connectionstatechange", () => {
                const connectionState = peerConnection.connectionState;
                log.log(`Peer connection state changed to ${connectionState} for client ${remoteClientId}`);
                if(connectionState === 'disconnected') this._cleanupPeerConnection(remoteClientId);

            })
        } catch (error) {
            log.error(`Error handling SDP offer: ${error.message}`);
            if (this.options.onError) {
                this.options.onError(error);
            }
            this._cleanupPeerConnection(remoteClientId);
        }
    }

    _syncLocalTracks(pc){
        if (!this.localStream) return;
        const [newTrack] = this.localStream.getVideoTracks();
        if (!newTrack) return;

        const sender = pc.getSenders()
                       .find(s => s.track && s.track.kind === newTrack.kind);

        if (sender) {
          if (sender.track !== newTrack) sender.replaceTrack(newTrack);
        } else {
          pc.addTrack(newTrack, this.localStream);
        }
    }

    _createPeerConnection(remoteClientId, onStatsReport) {
        const peerConnection = new RTCPeerConnection(this.configuration);
        this.peerConnectionByClientId[remoteClientId] = peerConnection;

        peerConnection.addEventListener('icecandidate', ({ candidate }) => {
            this._handleLocalIceCandidate(candidate, remoteClientId);
        });

        if (!this.peerConnectionStatsInterval) {
            this.peerConnectionStatsInterval = setInterval(() => {
                peerConnection.getStats().then(onStatsReport);
            }, 1000);
        }

        return peerConnection;
    }

    _handleLocalIceCandidate(candidate, remoteClientId) {
        if (candidate) {
            if (this.kinesis.options.useTrickleICE) {
                this.signalingClient.sendIceCandidate(candidate, remoteClientId);
            }
        } else {
            if (!this.kinesis.options.useTrickleICE) {
                const peerConnection = this.peerConnectionByClientId[remoteClientId];
                this.signalingClient.sendSdpAnswer(peerConnection.localDescription, remoteClientId);
            }
        }
    }

    _setupDataChannel(peerConnection, remoteClientId) {
        this.dataChannelByClientId[remoteClientId] = peerConnection.createDataChannel('kvsDataChannel');

        peerConnection.ondatachannel = event => {
            event.channel.onmessage = message => {
                console.log(`[PRODUCER] Received message: ${message.data}`);
            };
        };
    }

    async _createAndSendAnswer(peerConnection, remoteClientId) {
        const { sendAudio, sendVideo, useTrickleICE } = this.kinesis.options;
        await peerConnection.setLocalDescription(await peerConnection.createAnswer({ offerToReceiveAudio: sendAudio, offerToReceiveVideo: sendVideo }));
        if (useTrickleICE) {
            this.signalingClient.sendSdpAnswer(peerConnection.localDescription, remoteClientId);
        }
    }

    async _handleIceCandidate(candidate, remoteClientId) {
        try {
            const peerConnection = this.peerConnectionByClientId[remoteClientId];
            if (peerConnection) {
                await peerConnection.addIceCandidate(candidate);
            }
        } catch (error) {
            log.error(`Error adding ICE candidate: ${error.message}`);
            if (this.options.onError) {
                this.options.onError(error);
            }
        }
    }

    _closeSignalingClient() {
        if (this.signalingClient) {
            this.signalingClient.close();
            this.signalingClient = null;
        }
    }

    _clearPeerConnections() {
        Object.keys(this.peerConnectionByClientId).forEach(clientId => {
            const peerConnection = this.peerConnectionByClientId[clientId];
            if (peerConnection) {
                peerConnection.close();
                delete this.peerConnectionByClientId[clientId];
            }
        });
    }

    _clearStatsInterval() {
        if (this.peerConnectionStatsInterval) {
            clearInterval(this.peerConnectionStatsInterval);
            this.peerConnectionStatsInterval = null;
        }
    }

    _clearPingInterval(){
        if (this._wsPingInterval) clearInterval(this._wsPingInterval);
    }

    _cleanupPeerConnection(remoteClientId) {
        const peerConnection = this.peerConnectionByClientId[remoteClientId];
        if (peerConnection) {
            peerConnection.close();
            delete this.peerConnectionByClientId[remoteClientId];
        }
    }

    _delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
}

async function retryAsync(fn, testFinished = () => false, delay = 5000) {
    if(testFinished()) return;
    try {
        return await fn();
    } catch (error) {
        log.warning(`Retrying after error: ${error.message}.`);
        await new Promise(res => setTimeout(res, delay));
        return retryAsync(fn, testFinished, delay);
    }
}

export {
    Kinesis,
    StreamProducer
};
